import type { ICON } from '@george-labs.com/george-assets/icons';

import type { Transaction } from '@shared/transactions-types';
import { RoundingType } from '@shared/transactions-types';

import { iconsByCategory, iconsByName, iconsBySubCategory } from './CategoryIcons';
import { iconsByDirection } from './direction';
import { iconsByRounding } from './rounding';
import { hasRoundingTransaction } from '../predicates/hasRoundingTransaction';
import { hasAmountRoundupSaving } from '../predicates/hasAmountRoundupSaving';

export function getIconByName(name: string): ICON | undefined {
    return iconsByName[name as keyof typeof iconsByName];
}

export function getIconByCategory(category: string): ICON | undefined {
    return iconsByCategory[category as keyof typeof iconsByCategory];
}

export function getIconBySubCategory(subCategory: string): ICON | undefined {
    return iconsBySubCategory[subCategory as keyof typeof iconsBySubCategory];
}

export function getIconByRounding(rounding: keyof typeof iconsByRounding): ICON | undefined {
    return iconsByRounding[rounding];
}

export function getIconByDirection(direction: keyof typeof iconsByDirection): ICON | undefined {
    return iconsByDirection[direction];
}

export function getExtraAmountIcon(transaction: Transaction): ICON | undefined {
    const { roundingType } = transaction;

    if (hasRoundingTransaction(transaction)) {
        return getIconByRounding(roundingType ?? '');
    }

    if (hasAmountRoundupSaving()(transaction)) {
        return getIconByRounding(RoundingType.SAVING);
    }

    return undefined;
}

export function getTransactionIcon({
    categories,
    logo,
    txDirection,
}: Partial<Pick<Transaction, 'txDirection' | 'categories' | 'logo'>>): ICON | undefined {
    const [firstCategory] = categories ?? [];
    const { mainCategory, subCategory } = firstCategory ?? {};

    const iconFromLogo = logo?.uri ? getIconByName(logo?.uri) : undefined;
    const iconFromSubCategory = subCategory ? getIconBySubCategory(subCategory) : undefined;
    const iconFromCategory = mainCategory ? getIconByCategory(mainCategory) : undefined;
    const iconFromDirection = txDirection ? getIconByDirection(txDirection) : undefined;

    return iconFromLogo || iconFromSubCategory || iconFromCategory || iconFromDirection;
}
